/**
 * 把目前畫面上所有與 active symbol 相關的資料，bundle 成一段 markdown prompt，
 * 讓使用者可一鍵複製貼到 Gemini App / ChatGPT / 其他 LLM 做 Deep Research。
 *
 * 設計原則：
 * - 不丟「Recommendation 的最終結論」當權威，要把原始指標 + 我們的建議「並列」呈現，
 *   讓外部 LLM 自己重新判斷不被我們的判斷帶風向。
 * - 預設帶一段「請以資深台股短線交易員角度...」的提問範本，避免外部 LLM
 *   又跑「以下不構成投資建議」官腔。
 */

import type {
  Quote,
  Signal,
  TechnicalSnapshot,
  IntradayRange,
  DayTradeScore,
  Institutional,
  Margin,
  NewsItem,
} from '@invest-copilot/shared';
import type { Recommendation } from '../components/RecommendationCard';

interface Disposition {
  punish?: { announcedAt: string; fromDate: string; toDate: string; condition: string; measure: string; cumulativeCount: string };
  notice?: { date: string; cumulativeCount: string; reason: string }[];
}

export interface GeminiPromptInput {
  symbol: string;
  quote?: Quote;
  signal?: Signal;
  snapshot?: TechnicalSnapshot;
  intradayRange?: IntradayRange;
  dayTrade?: DayTradeScore;
  institutional?: Institutional;
  margin?: Margin;
  disposition?: Disposition;
  recommendation?: Recommendation;
  news?: NewsItem[];
}

function fmt(n: number | undefined | null, digits = 2): string {
  return typeof n === 'number' && Number.isFinite(n) ? n.toFixed(digits) : '—';
}

function signed(n: number | undefined | null): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return (n >= 0 ? '+' : '') + n.toLocaleString();
}

export function buildGeminiPrompt(input: GeminiPromptInput): string {
  const { symbol, quote, signal, snapshot, intradayRange, dayTrade, institutional, margin, disposition, recommendation, news } = input;
  const now = new Date();
  const nowStr = now.toLocaleString('zh-TW', { hour12: false });
  const isTW = symbol.endsWith('.TW') || symbol.endsWith('.TWO');

  const lines: string[] = [];

  // === Header ===
  lines.push(`# ${symbol}${quote?.shortName ? ' ' + quote.shortName : ''} · 即時資料快照`);
  lines.push(`> 擷取時間：${nowStr}　市場：${isTW ? '🇹🇼 台股' : '🇺🇸 美股'}　市場狀態：${quote?.marketState ?? 'unknown'}`);
  lines.push('');

  // === 即時報價 ===
  if (quote) {
    lines.push('## 即時報價');
    lines.push(`- 價：**${fmt(quote.price)}**（${quote.change >= 0 ? '▲' : '▼'} ${fmt(Math.abs(quote.change))}　${quote.changePct >= 0 ? '+' : ''}${fmt(quote.changePct)}%）`);
    lines.push(`- 昨收 ${fmt(quote.prevClose)} · 今日高 ${fmt(quote.dayHigh)} · 今日低 ${fmt(quote.dayLow)}`);
    if (quote.volume != null) lines.push(`- 成交量：${quote.volume.toLocaleString()}`);
    lines.push('');
  }

  // === 技術面 ===
  if (snapshot) {
    lines.push('## 技術指標 snapshot');
    lines.push(`- MA20 / MA60：${fmt(snapshot.ma20)} / ${fmt(snapshot.ma60)}`);
    lines.push(`- RSI14：${fmt(snapshot.rsi14, 1)}`);
    if (snapshot.macd) lines.push(`- MACD：macd=${fmt(snapshot.macd.macd, 3)} signal=${fmt(snapshot.macd.signal, 3)} hist=${fmt(snapshot.macd.hist, 3)}`);
    if (snapshot.bollinger) lines.push(`- Bollinger：%B=${fmt(snapshot.bollinger.percentB)}　上軌 ${fmt(snapshot.bollinger.upper)} / 中 ${fmt(snapshot.bollinger.mid)} / 下軌 ${fmt(snapshot.bollinger.lower)}`);
    if (snapshot.stochastic) lines.push(`- Stochastic：K=${fmt(snapshot.stochastic.k, 1)} / D=${fmt(snapshot.stochastic.d, 1)}`);
    if (snapshot.atrPct != null) lines.push(`- ATR%：${fmt((snapshot.atrPct ?? 0) * 100, 2)}%`);
    if (snapshot.volRatio != null) lines.push(`- 量比：${fmt(snapshot.volRatio)}`);
    if (snapshot.obvDivergence) lines.push(`- 量價背離：${snapshot.obvDivergence}`);
    if (snapshot.regime) lines.push(`- 市場狀態：${snapshot.regime}`);
    lines.push('');
  }

  // === 訊號 / 預估區間 ===
  if (signal) {
    lines.push('## 量化訊號');
    lines.push(`- Level：**${signal.level}**　綜合分 ${fmt(signal.score)}`);
    if (signal.factors?.length) {
      lines.push('- 因子：');
      for (const f of signal.factors.slice(0, 6)) {
        lines.push(`  - ${f.name} (${f.score >= 0 ? '+' : ''}${f.score})：${f.reason}`);
      }
    }
    lines.push('');
  }

  if (intradayRange) {
    lines.push('## 預估今日區間');
    lines.push(`- 高 ${fmt(intradayRange.estimatedHigh)} / 低 ${fmt(intradayRange.estimatedLow)}（振幅 ${fmt(intradayRange.estimatedRangePct)}%）`);
    lines.push('');
  }

  if (dayTrade) {
    lines.push('## 當沖適合度');
    lines.push(`- 分數 ${fmt(dayTrade.score, 0)} / 100　${dayTrade.label}　偏向：${dayTrade.bias}`);
    if (dayTrade.advice) lines.push(`- ${dayTrade.advice}`);
    lines.push('');
  }

  // === 籌碼（TW only）===
  if (isTW && institutional?.days?.length) {
    lines.push('## 三大法人（單位：張）');
    const latest = institutional.days[0]!;
    lines.push(`- 昨日（${latest.date}）：外資 ${signed(latest.foreign)} / 投信 ${signed(latest.trust)} / 自營 ${signed(latest.dealer)} / 合計 ${signed(latest.total)}`);
    lines.push(`- 近 5 日累計：外資 ${signed(institutional.totalForeign5d)} / 投信 ${signed(institutional.totalTrust5d)} / 自營 ${signed(institutional.totalDealer5d)} / 合計 ${signed(institutional.totalAll5d)}`);
    lines.push('');
  }

  if (isTW && margin?.days?.[0]) {
    const m = margin.days[0];
    lines.push('## 融資融券（張）');
    lines.push(`- 昨（${m.date}）融資餘額 ${m.marginBalance.toLocaleString()}（變化 ${signed(m.marginNetChange)}）`);
    lines.push(`- 昨融券餘額 ${m.shortBalance.toLocaleString()}（變化 ${signed(m.shortNetChange)}）`);
    lines.push(`- 近 5 日：融資 ${signed(margin.marginNetChange5d)} / 融券 ${signed(margin.shortNetChange5d)}`);
    if (margin.marginShortRatio != null) lines.push(`- 資券比 ${fmt(margin.marginShortRatio, 1)}　融資使用率 ${fmt(margin.marginUsageRate, 1)}%　當沖張 ${m.dayTrade.toLocaleString()}`);
    lines.push('');
  }

  if (isTW && disposition) {
    lines.push('## TWSE 處置 / 注意股狀態');
    if (disposition.punish) {
      const p = disposition.punish;
      lines.push(`- ⛔ **今日在處置名單**：${p.measure}（${p.condition}，累計第 ${p.cumulativeCount} 次）`);
      lines.push(`  期間 ${p.fromDate} ～ ${p.toDate}（公布日 ${p.announcedAt}）`);
      lines.push(`  限制：人工撮合約 5 分鐘一次、單筆 ≥10 張或當日累積 ≥30 張需預收款券、信用交易要足額。`);
    }
    if (disposition.notice?.length) {
      lines.push(`- ⚠ 近 7 日上注意 ${disposition.notice.length} 次：`);
      for (const n of disposition.notice.slice(0, 3)) {
        lines.push(`  - ${n.date}（累計 ${n.cumulativeCount}）${n.reason.slice(0, 80)}`);
      }
    }
    if (!disposition.punish && !disposition.notice?.length) lines.push('- ✅ 未在處置 / 注意名單');
    lines.push('');
  }

  // === Invest Copilot 自己的 AI 判斷（給外部 LLM 參考用，不是權威）===
  if (recommendation) {
    lines.push('## 🐧 Invest Copilot 內建 AI 判斷（僅供你參考、不必盲信）');
    lines.push(`- action：**${recommendation.action.toUpperCase()}**　timeWindow：${recommendation.timeWindow}　進場信心 ${Math.round(recommendation.confidence * 100)}%`);
    if (recommendation.action === 'buy') {
      lines.push(`- 進場 ${fmt(recommendation.entryPrice)} / 出場 ${fmt(recommendation.exitPrice)} / 停損 ${fmt(recommendation.stopLoss)}`);
      if (recommendation.entryTrigger) lines.push(`- 進場條件：${recommendation.entryTrigger}`);
    } else if (recommendation.skipReason) {
      lines.push(`- skipReason：${recommendation.skipReason}`);
    }
    if (recommendation.direction) lines.push(`- direction：${recommendation.direction} · horizon：${recommendation.horizon ?? '—'}`);
    if (recommendation.thesis) lines.push(`- thesis：${recommendation.thesis}`);
    lines.push(`- reasoning：${recommendation.reasoning}`);
    if (recommendation.failureConditions?.length) {
      lines.push('- failureConditions（失敗條件）：');
      for (const f of recommendation.failureConditions) lines.push(`  - ${f}`);
    }
    if (recommendation.keyDrivers?.length) {
      lines.push('- keyDrivers：');
      for (const d of recommendation.keyDrivers) {
        lines.push(`  - [${d.impact}/${d.weight}] **${d.factor}**：${d.note}`);
      }
    }
    lines.push(`- model：${recommendation.modelUsed}${recommendation.fallbackUsed ? ' (fallback)' : ''}`);
    lines.push('');
  }

  // === 新聞 ===
  if (news?.length) {
    lines.push('## 近期新聞（前 5 則）');
    for (const n of news.slice(0, 5)) {
      const date = n.publishedAt ? new Date(n.publishedAt).toLocaleDateString('zh-TW') : '';
      lines.push(`- ${date ? `(${date}) ` : ''}**${n.title}**${n.publisher ? ` — ${n.publisher}` : ''}`);
      if (n.url) lines.push(`  ${n.url}`);
    }
    lines.push('');
  }

  // === 提問範本 ===
  lines.push('---');
  lines.push('');
  lines.push('## 我想請你（Gemini）做：');
  lines.push('');
  lines.push(`你是一位資深${isTW ? '台股' : '美股'}短線交易員。請根據上面的即時資料：`);
  lines.push('');
  lines.push('1. **獨立評估**這檔現在該不該進場（不必順著 Invest Copilot 的判斷走，你可以反駁）');
  lines.push('2. 給出具體 **進場價 / 第一目標 / 停損價**（用實際數字，不要寫「等回測 MA20」這種抽象話）');
  lines.push('3. 列出 **2-4 條 thesis 失敗條件**（什麼跡象出現代表你錯了，要反守）');
  lines.push('4. 信心 0-100%。低於 50% 直接說「訊號不足」');
  lines.push('');
  lines.push('語氣：明確、不打官腔、不要寫「不構成投資建議」這種話 — 這是我私人決策參考。');

  return lines.join('\n');
}
